import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { supabase } from "@/lib/supabase";
import { Board, Column as ColumnType, CardStatus } from "@/types/board";
import { useToast } from "@/components/ui/use-toast";
import Column from "@/components/board/Column";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Bath, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { InspirationQuote } from "@/components/InspirationQuote";




export default function BoardView() {
  const [board, setBoard] = useState<Board | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [showBoardOptions, setShowBoardOptions] = useState(false);


  useEffect(() => {
    if (boardId) {
      fetchBoard(boardId);
    }
  }, [boardId]);

  // Add board update event listener
  useEffect(() => {
    const handleBoardUpdate = () => {
      if (boardId) {
        fetchBoard(boardId);
      }
    };

    boardEvents.on(BOARD_UPDATED, handleBoardUpdate);
    return () => {
      boardEvents.off(BOARD_UPDATED, handleBoardUpdate);
    };
  }, [boardId]);



  const fetchBoard = async (id: string) => {
    try {
      setIsLoading(true);

      // Fetch board with columns in a single query
      const { data: boardData, error: boardError } = await supabase
        .from("boards")
        .select(`
          id,
          title,
          owner_id,
          created_at,
          updated_at,
          columns (
            id,
            title,
            order,
            color,
            board_id,
            created_at,
            updated_at
          )
        `)
        .eq("id", id)
        .single();

      if (boardError) throw boardError;
      if (!boardData) {
        toast({
          title: "Error",
          description: "Board not found",
          variant: "destructive",
        });
        navigate("/boards");
        return;
      }

      let currentColumns = boardData.columns || [];

      // Check if we need to create default columns
      if (currentColumns.length === 0) {
        const defaultColumns = [
          { title: "To Do", color: "#fecaca", order: 0 },
          { title: "In Progress", color: "#fde68a", order: 1 },
          { title: "Done", color: "#bbf7d0", order: 2 }
        ];

        // First check if any columns exist (double-check to prevent race conditions)
        const { data: existingColumns } = await supabase
          .from("columns")
          .select("id")
          .eq("board_id", boardData.id);

        if (!existingColumns || existingColumns.length === 0) {
          // Insert all columns in a single operation
          const { data: newColumns, error: columnError } = await supabase
            .from("columns")
            .insert(
              defaultColumns.map(col => ({
                board_id: boardData.id,
                title: col.title,
                color: col.color,
                order: col.order
              }))
            )
            .select();

          if (columnError) throw columnError;
          if (newColumns) {
            currentColumns = newColumns;
          }
        } else {
          // Fetch full column data
          const { data: fullColumns } = await supabase
            .from("columns")
            .select("*")
            .eq("board_id", boardData.id);
          currentColumns = fullColumns || [];
        }
      }

      // Fetch cards for all columns
      const { data: cardsData, error: cardsError } = await supabase
        .from("cards")
        .select("*")
        .in("column_id", currentColumns.map(col => col.id))
        .order("order");

      if (cardsError) throw cardsError;

      // Combine the data
      const columnsWithCards: ColumnType[] = currentColumns
        .map(col => ({
          ...col,
          cards: (cardsData || [])
            .filter(card => card.column_id === col.id)
            .map(card => ({
              ...card,
              status: getCardStatus(col.title)
            }))
            .sort((a, b) => (a.order || 0) - (b.order || 0))
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const fullBoard: Board = {
        ...boardData,
        columns: columnsWithCards
      };

      setBoard(fullBoard);
    } catch (error: unknown) {
      console.error("Error fetching board:", error);
      // Only show error toast if we're not creating default columns
      if (error instanceof Error && error.message !== "Board not found") {
        toast({
          title: "Error",
          description: "Failed to load board",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCardStatus = (columnTitle: string): CardStatus => {
    const title = columnTitle.toLowerCase();
    if (title.includes('done') || title.includes('completed')) return 'done';
    if (title.includes('progress') || title.includes('doing')) return 'in_progress';
    return 'todo';
  };



  const handleEditCard = async (_columnId: string, cardId: string, title: string, description: string) => {
    try {
      const { error } = await supabase
        .from("cards")
        .update({ title, description })
        .eq("id", cardId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col => ({
            ...col,
            cards: col.cards.map(card =>
              card.id === cardId
                ? { ...card, title, description }
                : card
            )
          }))
        };
      });

      toast({
        title: "Success",
        description: "Card updated successfully",
      });
    } catch (error) {
      console.error("Error updating card:", error);
      toast({
        title: "Error",
        description: "Failed to update card",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCard = async (_columnId: string, cardId: string) => {
    try {
      const { error } = await supabase
        .from("cards")
        .delete()
        .eq("id", cardId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col => ({
            ...col,
            cards: col.cards.filter(card => card.id !== cardId)
          }))
        };
      });

      toast({
        title: "Success",
        description: "Card deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting card:", error);
      toast({
        title: "Error",
        description: "Failed to delete card",
        variant: "destructive",
      });
    }
  };

  const handleEditColumn = async (columnId: string, title: string) => {
    try {
      const { error } = await supabase
        .from("columns")
        .update({ title })
        .eq("id", columnId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId
              ? { ...col, title }
              : col
          )
        };
      });

      toast({
        title: "Success",
        description: "Column updated successfully",
      });
    } catch (error) {
      console.error("Error updating column:", error);
      toast({
        title: "Error",
        description: "Failed to update column",
        variant: "destructive",
      });
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    try {
      const { error } = await supabase
        .from("columns")
        .delete()
        .eq("id", columnId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.filter(col => col.id !== columnId)
        };
      });

      toast({
        title: "Success",
        description: "Column deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting column:", error);
      toast({
        title: "Error",
        description: "Failed to delete column",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || !board) return;

    const { source, destination, draggableId } = result;

    // If dropped in the same spot
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Find the source and destination columns
    const sourceCol = board.columns.find(col => col.id === source.droppableId);
    const destCol = board.columns.find(col => col.id === destination.droppableId);

    if (!sourceCol || !destCol) return;

    try {
      // Update the card's column and order
      const { error } = await supabase
        .from("cards")
        .update({
          column_id: destination.droppableId,
          order: destination.index
        })
        .eq("id", draggableId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;

        const newColumns = prev.columns.map(col => {
          // Remove from source column
          if (col.id === source.droppableId) {
            const newCards = [...col.cards];
            const [removed] = newCards.splice(source.index, 1);
            if (!removed) return col; // Should not happen
            return { ...col, cards: newCards };
          }
          // Add to destination column
          if (col.id === destination.droppableId) {
            const newCards = [...col.cards];
            const [removed] = sourceCol.cards.splice(source.index, 1);
            newCards.splice(destination.index, 0, {
              ...removed,
              column_id: destination.droppableId,
              status: getCardStatus(col.title)
            });
            return { ...col, cards: newCards };
          }
          return col;
        });

        return { ...prev, columns: newColumns };
      });
    } catch (error) {
      console.error("Error updating card position:", error);
      toast({
        title: "Error",
        description: "Failed to update card position",
        variant: "destructive",
      });
    }
  };

  const handlePriorityChange = async (cardId: string, priority: 'critical' | 'important' | 'normal') => {
    try {
      const { error } = await supabase
        .from("cards")
        .update({ priority })
        .eq("id", cardId);

      if (error) throw error;

      // Update local state
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: prev.columns.map(col => ({
            ...col,
            cards: col.cards.map(card =>
              card.id === cardId
                ? { ...card, priority }
                : card
            )
          }))
        };
      });

      toast({
        title: "Success",
        description: "Card priority updated successfully",
      });
    } catch (error) {
      console.error("Error updating card priority:", error);
      toast({
        title: "Error",
        description: "Failed to update card priority",
        variant: "destructive",
      });
    }
  };

  const handleAddColumn = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!board) return;

      // First insert the column
      const { error: insertError } = await supabase
        .from("columns")
        .insert({
          board_id: board.id,
          title: newColumnTitle,
          color: "#4b5563",
          order: board.columns.length || 0
        });

      if (insertError) throw insertError;

      // Then fetch the newly created column
      const { data: newColumn, error: fetchError } = await supabase
        .from("columns")
        .select("*")
        .eq("board_id", board.id)
        .eq("title", newColumnTitle)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (fetchError) throw fetchError;

      // Update local state with the new column
      setBoard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          columns: [...prev.columns, { ...newColumn, cards: [] }]
        };
      });

      toast({
        title: "Success",
        description: "Column added successfully",
      });

      setNewColumnTitle('');
      setIsAddingColumn(false);
    } catch (error) {
      console.error("Error adding column:", error);
      toast({
        title: "Error",
        description: "Failed to add column",
        variant: "destructive",
      });
    }
  };

  const handleBoardTitleChange = async (newTitle: string) => {
    try {
      if (!board) return;

      const { error } = await supabase
        .from("boards")
        .update({ title: newTitle })
        .eq("id", board.id);

      if (error) throw error;

      setBoard(prev => prev ? { ...prev, title: newTitle } : null);

      toast({
        title: "Success",
        description: "Board title updated successfully",
      });
    } catch (error) {
      console.error("Error updating board title:", error);
      toast({
        title: "Error",
        description: "Failed to update board title",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBoard = async () => {
    try {
      const { error } = await supabase
        .from('boards')
        .delete()
        .eq('id', board?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Board deleted successfully",
      });

      navigate('/boards');
    } catch (error) {
      console.error('Error deleting board:', error);
      toast({
        title: "Error",
        description: "Failed to delete board",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-lg">Loading board...</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg text-red-500">Board not found</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <InspirationQuote />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/boards">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-300 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to My DoDue Space
                </Button>
              </Link>
              <h1 className="text-xl font-semibold text-white">
                {board?.title || "Loading..."}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Board
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-300 hover:text-white"
              >
                <Bath className="h-4 w-4 mr-2" />
                Flush
              </Button>
            </div>
          </div>

          {/* Add the CardLimitWarning component here */}

        </div>

        {/* Board Content */}
        <div className="p-4 md:p-6">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="board" direction="horizontal" type="COLUMN">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="
                    flex flex-col md:grid 
                    md:grid-cols-4 
                    gap-4 
                    pb-4 
                    min-h-[calc(100vh-12rem)]
                    overflow-x-auto
                    md:overflow-x-visible
                  "
                >
                  {board?.columns.map((column, index) => (
                    <div key={column.id} className="w-full flex-shrink-0">
                      <Column
                        column={column}
                        index={index}
                        onEditCard={handleEditCard}
                        onDeleteCard={handleDeleteCard}
                        onEditColumn={handleEditColumn}
                        onDeleteColumn={handleDeleteColumn}
                        onPriorityChange={handlePriorityChange}
                      />
                    </div>
                  ))}
                  {provided.placeholder}
                  <Button
                    onClick={() => setIsAddingColumn(true)}
                    className="h-[200px] w-full border-2 border-dashed border-gray-600 hover:border-gray-400 bg-transparent text-gray-400 hover:text-gray-200 flex flex-col items-center justify-center gap-2"
                  >
                    <Plus className="h-6 w-6" />
                    Add Column
                  </Button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {/* Add Column Dialog */}
        <Dialog open={isAddingColumn} onOpenChange={setIsAddingColumn}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Column</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Enter column title"
                  value={newColumnTitle}
                  onChange={(e) => setNewColumnTitle(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddingColumn(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddColumn}>Add Column</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Board Options Dialog */}
        <Dialog open={showBoardOptions} onOpenChange={setShowBoardOptions}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>DoDue Project Options</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="boardTitle">Board Title</Label>
                <Input
                  id="boardTitle"
                  placeholder="Enter board title"
                  value={board?.title || ""}
                  onChange={(e) => handleBoardTitleChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Button
                  variant="destructive"
                  onClick={handleDeleteBoard}
                  className="w-full"
                >
                  Delete Board
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBoardOptions(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="bg-slate-800 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle>Delete Board</DialogTitle>
              <DialogDescription className="text-gray-400">
                Are you sure you want to delete "{board?.title}"? This action cannot be undone.
                All cards and columns will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="border-gray-600 text-gray-300 hover:text-white hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleDeleteBoard();
                  setShowDeleteConfirm(false);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Board
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}